<template>
  <v-sheet id="annotationControl" v-hotkey="keymap" elevation="4" class=" pa-2">
    <div class="text-h6 text-center">
      {{ $t("annotation.visualization.toolsTitle") }}
    </div>
    <div class="d-flex align-center justify-center">
      <v-checkbox
        :label="$t('annotation.visualization.floatMode')"
        v-model="floatModeActive"
      ></v-checkbox>
    </div>
    <div class="d-flex mt-2 mb-2" ref="annotationManipulationControls"></div>
    <v-divider></v-divider>
    <div class="text-h6 text-center">
      {{ $t("annotation.visualization.classesTitle") }}
    </div>
    <v-list class="pt-3" ref="annotationClassList"></v-list>
    <v-divider></v-divider>
    <div class="d-flex">
      <!--      <v-tooltip right>-->
      <!--        <template v-slot:activator="{ on, attr }">-->
      <!--          <v-icon v-on="on" v-bind="attr">mdi-drag-horizontal-variant</v-icon>-->
      <!--        </template>-->
      <!--        <span>Hold and drag around</span>-->
      <!--      </v-tooltip>-->
      <v-tooltip right>
        <template v-slot:activator="{ on, attr }">
          <v-btn
            block
            tile
            text
            v-on="on"
            v-bind="attr"
            @click="$emit('next-slide')"
            >{{ $t("annotation.visualization.nextWSI") }}</v-btn
          >
        </template>
        <span>{{ $t("annotation.visualization.nextWSITooltip") }}</span>
      </v-tooltip>
    </div>
    <!-- New annotation dialog -->
    <v-dialog
      v-if="dialogNewAnnotation"
      max-width="600"
      v-model="dialogNewAnnotation"
      persistent
      v-hotkey="{ enter: storeAnnotation }"
    >
      <v-form @submit.prevent="storeAnnotation">
        <v-card>
          <v-card-title>
            {{ $t("annotation.visualization.newAnnotationTitle") }}
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <p class="body-1">
                  {{ $t("annotation.visualization.newAnnotationSubtitle") }}<code>Enter</code>
                </p>
              </v-row>
              <v-row>
                <v-textarea
                  :label="$t('annotation.visualization.newAnnotationDescription')"
                  v-model="dialogNewAnnotationData.description"
                ></v-textarea>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
              <v-btn text color="red" @click="cancelAnnotation">
                {{ $t("annotation.visualization.newAnnotationDiscard") }}
              </v-btn>
              <v-btn type="submit" text>
                {{ $t("annotation.visualization.newAnnotationSave") }}
              </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </v-sheet>
</template>

<script>
import Vue from "vue";
import AnnotationClassControl from "@/components/slide/controls/AnnotationClassControl";
import DrawAnnotationControl from "@/viewer/DrawAnnotationControl";
import SelectControl from "@/viewer/SelectControl";
import DeleteControl from "@/viewer/DeleteControl";
import ManipulationControl from "@/components/slide/controls/ManipulationControl";
import { deleteAnnotation, storeAnnotation } from "@/api/craiyon";
import { deleteResource } from "@/api/fhir";
import { uuidv4 } from "@/utils/deeppath-utils";
import {
  FEATURE_ANNOTATION_ID,
  FEATURE_OBSERVATION_ID,
  FEATURE_STATUS
} from "@/constants";
import { FIND } from "@/store/modules/annotationClass";
import { mapGetters } from "vuex";

export default {
  props: {
    slide: Object,
    viewer: Object,
    observations: Array,
    floatMode: Boolean,
    currentSelectedRawAnnotation: Object
  },
  data() {
    return {
      dialogNewAnnotation: false,
      dialogNewAnnotationData: {
        feature: null,
        description: null
      },
      currentActiveControl: null,
      manipulationControls: [],
      currentSelectedAnnotation: null,
      floatModeActive: false,
      runningOperationsStack: [],
      drawControls: []
    };
  },
  computed: {
    ...mapGetters({ annotationClass: FIND }),
    map() {
      return this.viewer.map;
    },
    keymap() {
      return {
        d: this.toggleManipulationDelete,
        e: this.toggleManipulationModify,
        s: this.toggleManipulationSelect
      };
    },
    hasRunningOperations() {
      const val = this.runningOperationsStack.length > 0;
      return val;
    },
    selectTooltip() {
      return this.$t("annotation.visualization.selectTooltip");
    },
    deleteTooltip() {
      return this.$t("annotation.visualization.deleteTooltip");
    }
  },
  watch: {
    hasRunningOperations(val) {
      this.$emit("running-operations:changed", val);
    },
    floatModeActive(val) {
      this.$emit("float-mode:changed", val);
    },
    currentSelectedAnnotation(val) {
      if (val === null || val === undefined) {
        this.$emit("annotation:hide-details");
      }
    }
  },
  methods: {
    toggleManipulationDelete() {
      this.activateManipulationControl("delete");
    },
    toggleManipulationModify() {
      this.activateManipulationControl("modify");
    },
    toggleManipulationSelect() {
      this.activateManipulationControl("select");
    },
    activateManipulationControl(action) {
      // deactivate draw controls
      this.drawControls.forEach(e => e.deactivate());
      const component = this.manipulationControls.find(
        e => e.action === action
      );
      component.click();
    },
    renderAnnotationClassControls() {
      const annotationClassControlList = this.$refs.annotationClassList.$el;
      const source = this.viewer.editLayer.getSource();
      const layer = this.viewer.editLayer;

      for (let ac of this.slide.annotation_class) {
        const annotationClass = this.annotationClass(ac);
        const componentClass = Vue.extend(AnnotationClassControl);
        const componentInstance = new componentClass({
          propsData: {
            annotationClass
          }
        });
        componentInstance.$mount();

        const annotationControl = new DrawAnnotationControl({
          optElement: componentInstance.$el,
          optTarget: annotationClassControlList,
          optSource: source,
          optType: annotationClass.annotation_geometry,
          optFillColor: annotationClass.fill_color,
          optStrokeColor: annotationClass.color,
          optCallback: this.endDrawAnnotation,
          optAnnotationClass: annotationClass,
          layer
        });
        this.drawControls.push(annotationControl);
        componentInstance.control = annotationControl;
        annotationControl.setMap(this.map);
        componentInstance.$on("click", e =>
          this.onClickControl(e, componentInstance)
        );
      }
    },
    renderAnnotationManipulationControls() {
      const manipulationControls = [
        {
          callback: this.selectAnnotation,
          control: SelectControl,
          props: {
            tooltip: this.selectTooltip,
            action: "select"
          }
        },
        // {
        //   callback: this.modifyAnnotation,
        //   control: ModifyControl,
        //   props: {
        //     tooltip: "Modify annotation",
        //     action: "modify"
        //   }currentSelectedRawAnnotation
        // },
        {
          callback: this.deleteAnnotation,
          control: DeleteControl,
          props: {
            tooltip: this.deleteTooltip,
            action: "delete"
          }
        }
      ];
      this.manipulationControls = [];
      const target = this.$refs["annotationManipulationControls"];
      const source = this.viewer.editLayer.getSource();
      for (let mc of manipulationControls) {
        const componentClass = Vue.extend(ManipulationControl);
        const componentInstance = new componentClass({
          propsData: mc.props
        });
        componentInstance.$mount();
        this.manipulationControls.push(componentInstance);
        const annotationControl = new mc.control({
          element: componentInstance.$el,
          target,
          source,
          callback: mc.callback
        });
        annotationControl.setMap(this.map);
        componentInstance.control = annotationControl;
        componentInstance.$on("click", e =>
          this.onClickControl(e, componentInstance)
        );
      }
    },
    onClickControl(e, componentInstance) {
      this.currentSelectedAnnotation = null;
      if (this.currentActiveControl !== null) {
        // deactivate the current control and remove the highlighting
        this.deactivateCurrentControl();
        // if button is pressed a second time
        if (componentInstance.type === "mutation") {
          if (this.toggleControl(e.action)) {
            return;
          }
        }
        if (componentInstance.type === "annotationClass") {
          if (this.toggleControl(e.annotationClass.annotation_geometry)) {
            return;
          }
        }
      }

      this.currentActiveControl = componentInstance;
      componentInstance.active = true;
      e.control.activate();
    },

    toggleControl(currentAction) {
      this.currentSelectedAnnotation = null;
      if (!this.currentActiveControl) {
        return false;
      }
      if (currentAction === this.currentActiveControl.action) {
        this.currentActiveControl.active = false;
        this.currentActiveControl.control.deactivate();
        this.currentActiveControl = null;
        return true;
      }
      return false;
    },
    deactivateCurrentControl() {
      if (this.currentActiveControl !== null) {
        // deactivate the current control and remove the highlighting
        this.currentActiveControl.active = false;
        this.currentActiveControl.control.deactivate();
        const currentAction = this.currentActiveControl.action;
        if (currentAction === "select" || currentAction === "delete") {
          // remove selected features
          this.currentActiveControl.control.interaction.getFeatures().clear();
        }
      }
      this.currentSelectedAnnotation = null;
    },
    endDrawAnnotation(e) {
      if (
        e.annotationClass.annotation_geometry === "Polygon" &&
        this.arrayIntersect(e.target.sketchLineCoords_)
      ) {
        //TODO show notification
        //TODO fix uncaught error
        this.cancelAnnotation();
      } else {
        this.dialogNewAnnotation = true;
        this.dialogNewAnnotationData.feature = e;
      }
      // TODO focus on field name
      // this.$refs["inputAnnotationName"].$refs.input.focus();
    },
    selectAnnotation(e) {
      console.log(e);
      // interaction.getFeatures().select()
      if (e.selected.length === 0) {
        this.currentSelectedAnnotation = null;
        return;
      }
      const observationId = e.selected[0].get(FEATURE_OBSERVATION_ID);
      const observation = this.observations.find(e => e.id === observationId);
      this.currentSelectedAnnotation = observation;
      this.$emit("annotation:selected", observation);
    },
    async storeAnnotation() {
      this.runningOperationsStack.push(true);
      const e = this.dialogNewAnnotationData.feature;
      if (
        this.dialogNewAnnotationData.description === null ||
        this.dialogNewAnnotationData.description === undefined
      ) {
        this.dialogNewAnnotationData.description = "";
      }
      const feature = e.feature;
      const annotation = {
        wsiId: this.slide.id,
        geometry: feature,
        slide: this.slide.fhir_identifier,
        annotationClassId: e.annotationClass.id,
        description: this.dialogNewAnnotationData.description,
        project: this.slide.project
      };
      // emulate observation and assign a temporary id
      // the observation will be replaced by the one coming from the server
      // once it was persisted
      const uuid = uuidv4();
      feature.set(FEATURE_OBSERVATION_ID, uuid);
      this.$emit("annotation:created", {
        resourceType: "Observation",
        id: uuid,
        code: {
          coding: [
            {
              code: e.annotationClass.id
            }
          ]
        },
        note: [
          {
            text: this.dialogNewAnnotationData.description
          }
        ]
      });
      // reset
      this.resetDialogNewAnnotation();
      // this.deactivateCurrentControl();

      let res = await storeAnnotation(this.token, annotation);
      console.log(res);
      feature.set(FEATURE_ANNOTATION_ID, res.id);
      feature.set(FEATURE_OBSERVATION_ID, res.observation_id);
      feature.set(FEATURE_STATUS, "created");

      this.$emit("annotation:stored", { id: res.observation_id, uuid });
      this.runningOperationsStack.pop();
    },
    async deleteAnnotation(e) {
      return new Promise((resolve, reject) => {
        if (e.selected.length === 0) {
          reject();
          return;
        }
        const status = e.selected[0].get(FEATURE_STATUS);

        if (status !== "created") {
          // TODO show some notification
          reject();
          return;
        }
        this.runningOperationsStack.push(true);
        const id = e.selected[0].get(FEATURE_ANNOTATION_ID);
        deleteAnnotation(this.token, id).then(data => resolve());
        this.runningOperationsStack.pop();
      });
    },
    resetDialogNewAnnotation() {
      this.dialogNewAnnotationData = {};
      this.dialogNewAnnotation = false;
    },
    cancelAnnotation() {
      const e = this.dialogNewAnnotationData.feature.feature;
      this.viewer.editLayer.getSource().removeFeature(e);
      this.resetDialogNewAnnotation();
      this.deactivateCurrentControl();
    },
    modifyAnnotation(e) {
      console.log("modify annotation on server", e);
    },
    intersects(a, b, c, d, p, q, r, s) {
      var det, gamma, lambda;
      det = (c - a) * (s - q) - (r - p) * (d - b);
      if (det === 0) {
        return false;
      } else {
        lambda = ((s - q) * (r - a) + (p - r) * (s - b)) / det;
        gamma = ((b - d) * (r - a) + (c - a) * (s - b)) / det;
        return 0 < lambda && lambda < 1 && 0 < gamma && gamma < 1;
      }
    },
    arrayIntersect(array) {
      let tmpArray = array;
      tmpArray.push(array[0]);
      for (let i = 0; i < tmpArray.length - 1; i += 1) {
        for (let j = i + 2; j < tmpArray.length - 1; j += 1) {
          // Anchor line
          const point_0 = tmpArray[i];
          const point_1 = tmpArray[i + 1];
          // Test line
          const point_2 = tmpArray[j];
          const point_3 = tmpArray[j + 1];
          if (
            this.intersects(
              point_0[0],
              point_0[1],
              point_1[0],
              point_1[1],
              point_2[0],
              point_2[1],
              point_3[0],
              point_3[1]
            )
          ) {
            return true;
          }
        }
      }
      return false;
    }
  },
  mounted() {
    this.renderAnnotationClassControls();
    this.renderAnnotationManipulationControls();
    this.floatModeActive = this.floatMode;
  }
};
</script>

<style scoped></style>
